import * as Sentry from '@sentry/react';
import { AuthenticatedRoutesGuard, getOrgSlugFromUrl, NonAuthenticatedRoutesGuard, TFNotFoundRoute } from '@tf/shared';
import { DatesProvider } from '@tf/ui';
import type React from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';
import { AccountOverview, AccountPaymentsTable } from '@/components/accounts';
import { CasesTable, ProfileCaseRoute } from '@/components/cases';
import { AutoAssignedSettings, CraSettingsTable } from '@/components/cra-settings';
import { ApplyConfigurationPreviewPatch } from '@/components/internal-tools/configurationPreview';
import { EnumsRoute } from '@/components/internal-tools/EnumsRoute';
import { ExportRoute } from '@/components/internal-tools/ExportRoute';
import { ImportRoute } from '@/components/internal-tools/ImportRoute';
import { InternalToolsLayout } from '@/components/internal-tools/InternalToolsLayout';
import { StructsRoute } from '@/components/internal-tools/StructsRoute';
import { MonitoringCardAssociations, MonitoringCardMatches } from '@/components/monitoring';
import { PaymentCaseRoute, PaymentOverview, ScreeningsTable } from '@/components/payments';
import { ScreeningResults } from '@/components/payments/ScreeningResults';
import { ConnectionsTable } from '@/components/profiles/ConnectionsTable';
import { ReviewForm } from '@/components/reviews';
import { EntityReviewForm } from '@/components/reviews/EntityReviewForm';
import { UserInvitationsTable, UserTable } from '@/components/settings';
import { AppLayout } from '@/components/shared';
import { I18nProvider } from '@/core/providers/AppProvider/I18nProvider';
import { StateProvider } from '@/core/providers/AppProvider/StateProvider';
import { AcceptInvitationRoute } from '@/routes/AcceptInvitationRoute';
import { CRASettingsLayout } from '@/routes/CRASettingsLayout';
import { DataExportRoute } from '@/routes/DataExportRoute';
import { EntityListRoute } from '@/routes/EntitytListRoute';
import InstantCheckRoute from '@/routes/InstantCheckRoute';
import { LoginRoute } from '@/routes/LoginRoute';
import { AccountCardLayout } from '@/routes/layouts/AccountCardLayout';
import { AccountEntityLayout } from '@/routes/layouts/AccountEntityLayout';
import { AccountReviewLayout } from '@/routes/layouts/AccountReviewLayout';
import { AccountTabsLayout } from '@/routes/layouts/AccountTabsLayout';
import { EntityLayout } from '@/routes/layouts/EntityLayout';
import {
	InstantCheckCardLayout,
	InstantCheckHitsLayout,
	InstantCheckSearchLayout,
} from '@/routes/layouts/InstantCheckLayout';
import {
	MonitoringCardLayout,
	MonitoringCardTabsLayout,
	MonitoringMatchLayout,
} from '@/routes/layouts/MonitoringLayout';
import { NavigateToFirstAccountEntityFormRoute } from '@/routes/layouts/NavigateToFirstAccountEntityFormRoute';
import { NavigateToFirstEntityFormRoute } from '@/routes/layouts/NavigateToFirstEntityFormRoute';
import { NavigateToFirstFormRoute } from '@/routes/layouts/NavigateToFirstFormRoute';
import { UserTablesLayout } from '@/routes/layouts/UserTablesLayout';
import { MonitoringRoute } from '@/routes/MonitoringRoute';
import { PaymentsListRoute } from '@/routes/PaymentsListRoute';
import { ProfileGroupsRoute } from '@/routes/ProfileGroupsRoute';
import { ProfileRedirectRoute } from '@/routes/ProfileRedirectRoute';
import { SignOutRoute } from '@/routes/SignOutRoute';
import { TokenListRoute } from '@/routes/TokenListRoute';
import { UserCardRoute } from '@/routes/UserCardRoute';
import { UserInvitationRoute } from '@/routes/UserInvitationRoute';

const AuthZoneGuardAndProviders: React.FC = () => {
	return (
		<AuthenticatedRoutesGuard
			signInPagePath="/sign-in"
			checkOrgMembershipFn={(membership) => {
				let requiredOrgSlug = getOrgSlugFromUrl();
				// Allow TF to be open as localhost:4040 too
				// Needed for internal-tools, Google doesn't support localhost subdomains
				if (import.meta.env.DEV) {
					requiredOrgSlug = requiredOrgSlug === 'localhost' ? 'local' : requiredOrgSlug;
				}
				const isRequiredOrgActive = requiredOrgSlug === membership.organization.slug?.toLowerCase();

				const isOwnerOrHasCapabilities =
					membership.role === 'admin' ||
					(Array.isArray(membership.publicMetadata.capabilities) && membership.publicMetadata.capabilities.length > 0);

				return isRequiredOrgActive && isOwnerOrHasCapabilities;
			}}
			trackOrgMismatchFn={(membership) => {
				Sentry.captureMessage('User is not a member of required organization', {
					level: 'error',
					extra: {
						hostname: window.location.hostname,
						requiredOrgSlug: getOrgSlugFromUrl(),
						activeOrgSlug: membership.organization.slug?.toLowerCase(),
						capabilities: membership.publicMetadata.capabilities,
						role: membership.role,
					},
				});
			}}
		>
			<StateProvider>
				{/* ApplyConfigurationPreviewPatch should be inside StateProvider because it needs initialized GraphQLClient */}
				<ApplyConfigurationPreviewPatch />
				<I18nProvider>
					<DatesProvider>
						<Outlet />
					</DatesProvider>
				</I18nProvider>
			</StateProvider>
		</AuthenticatedRoutesGuard>
	);
};

export const Router: React.FC = () => {
	return (
		<Routes>
			<Route path="accept-invite/*" element={<AcceptInvitationRoute />} />
			<Route path="sign-out" element={<SignOutRoute />} />

			{/*non-authenticated users*/}
			<Route path="/" element={<NonAuthenticatedRoutesGuard startPagePath="/" />}>
				<Route path="/sign-in/*" element={<LoginRoute />} />
			</Route>

			{/* authenticated routes */}
			<Route path="/" element={<AuthZoneGuardAndProviders />}>
				<Route element={<AppLayout />}>
					<Route index element={<ProfileRedirectRoute />} />

					<Route path="settings/users" element={<UserTablesLayout />}>
						<Route index element={<UserTable />} />
						<Route path="invitations" element={<UserInvitationsTable />} />
					</Route>

					<Route path="settings/tokens" element={<TokenListRoute />} />

					<Route path="settings/cra" element={<CRASettingsLayout />}>
						<Route index element={<CraSettingsTable />} />
						<Route path="auto-assigned" element={<AutoAssignedSettings />} />
					</Route>

					<Route path="settings/invitations/invite" element={<UserInvitationRoute />} />
					<Route path="settings/users/:userId" element={<UserCardRoute />} />
					<Route path="accounts/:accountId" element={<AccountCardLayout />}>
						<Route element={<AccountTabsLayout />}>
							<Route index element={<AccountOverview />} />

							<Route path="cases" element={<CasesTable />} />
							<Route path="cases/:caseId" element={<ProfileCaseRoute />} />

							<Route path="payments" element={<AccountPaymentsTable />} />

							<Route path="connections" element={<ConnectionsTable />} />

							<Route path=":connectionKind" element={<AccountReviewLayout />}>
								<Route index element={<NavigateToFirstFormRoute />} />
								<Route path=":connectionId" element={<NavigateToFirstFormRoute />} />
								<Route path=":connectionId/:formKind" element={<ReviewForm />} shouldRevalidate={() => true} />
							</Route>
						</Route>
						<Route path=":connectionKind/:connectionId/entity/:entityId" element={<AccountEntityLayout />}>
							<Route index element={<NavigateToFirstAccountEntityFormRoute />} />
							<Route path=":formKind" element={<ReviewForm />} shouldRevalidate={() => true} />
						</Route>
					</Route>

					<Route path=":groupLabel" element={<ProfileGroupsRoute />} />

					<Route path="data-export" element={<DataExportRoute />} />

					<Route path="instant-check" element={<InstantCheckRoute />}>
						<Route index element={<InstantCheckSearchLayout />} />
						<Route path="hits" element={<InstantCheckHitsLayout />} />
						<Route path="card" element={<InstantCheckCardLayout />} />
					</Route>

					<Route path="entities">
						<Route index element={<EntityListRoute />} />
						<Route path=":entityId" element={<EntityLayout />}>
							<Route index element={<NavigateToFirstEntityFormRoute />} />
							<Route path=":formKind" element={<EntityReviewForm />} />
						</Route>
					</Route>

					<Route path="payments">
						<Route index element={<PaymentsListRoute />} />
						<Route path=":paymentId" element={<PaymentOverview />} />
						<Route path=":paymentId/assessments/:assessmentId/screenings" element={<ScreeningsTable />} />
						<Route path=":paymentId/assessments/:assessmentId/screenings/:screeningId" element={<ScreeningResults />} />
						<Route path=":paymentId/cases/:caseId" element={<PaymentCaseRoute />} />
					</Route>

					<Route path="monitoring">
						<Route index element={<MonitoringRoute />} />
						<Route path=":graphId" element={<MonitoringCardLayout />}>
							<Route element={<MonitoringCardTabsLayout />}>
								<Route index element={<MonitoringCardMatches />} />
								<Route path="associations" element={<MonitoringCardAssociations />} />
							</Route>
							<Route path="matches/:matchId" element={<MonitoringMatchLayout />} />
						</Route>
					</Route>

					<Route path="internal-tools" element={<InternalToolsLayout />}>
						<Route path="enums/:enumName?" element={<EnumsRoute />} />
						<Route path="structs/:structName?" element={<StructsRoute />} />
						<Route path="export" element={<ExportRoute />} />
						<Route path="import" element={<ImportRoute />} />
					</Route>
				</Route>
			</Route>

			<Route path="*" element={<TFNotFoundRoute />} />
		</Routes>
	);
};
